import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { line, fill, IMPORT_SCREEN_ROUTE, keySegment, Size, t, UNDO_MERGE_GROUPS, ZOHO_BOOKS_RUNS_PATH, type ImportMergeField, type ImportRunView, type StringKey, type UndoMergeAnswer, type UndoMergeGroup, type UndoMergePreview, type UndoMergeRow } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Checkbox } from '../../components/controls/Checkbox';
import { FormFrame } from '../../components/forms/FormFrame';
import { post, useApi } from '../records/api';
import styles from './UndoMergeWindow.module.css';

/** 🔒 The three routes of decision 7500: the Merged view's list, one merge's window, and its press. */
export const DUPLICATES_MERGES_PATH = '/duplicates/merges';
/** The window's read; a company merge that recorded nothing names the Zoho references chosen for the returned record. */
export const undoMergePreviewPath = (mergeId: string, zoho: readonly string[] | null = null): string =>
  `${DUPLICATES_MERGES_PATH}/${encodeURIComponent(mergeId)}/undo-preview${zoho === null ? '' : `?${new URLSearchParams({ zoho: zoho.join(',') }).toString()}`}`;
export const undoMergePath = (mergeId: string): string => `${DUPLICATES_MERGES_PATH}/${encodeURIComponent(mergeId)}/undo`;

const GROUP_WORD: Readonly<Record<UndoMergeGroup, string>> = {
  belonged: t('undoMerge.word.goesBack'),
  madeAfter: t('undoMerge.word.stays'),
  cantTell: t('undoMerge.word.cantTell'),
};

/** A row's kind in words: Document, Person, Site. */
const kindWord = (row: UndoMergeRow): string => t(`undoMerge.kind.${row.kind}` as const);

/** A value an import Merge wrote, as a person reads it: a flag as Yes or No, nothing as empty. */
function valueWords(value: unknown): string {
  if (value === null || value === undefined || value === '') return t('undoMerge.importEmpty');
  if (typeof value === 'boolean') return value ? t('control.yes') : t('control.no');
  return String(value);
}

/** 🔒 The fields an import Merge wrote, which stay on the survivor, each with its value before (decision 7550). */
function wroteWords(wrote: readonly ImportMergeField[] | null, intoName: string): string {
  if (wrote === null) return t('undoMerge.importWroteUnknown');
  if (wrote.length === 0) return t('undoMerge.importWroteNone');
  const fields = wrote.map((one) => `${t(`import.field.${keySegment(one.field)}` as StringKey)} ${valueWords(one.to)} (${t('undoMerge.importWas')} ${valueWords(one.from)})`);
  return `${t('undoMerge.importWrote')} ${intoName}: ${fields.join('; ')}.`;
}

/**
 * 🔒 Undo Merge (decision 7500; U1b): the merged record returns with its own
 * fields, and what moved is listed in three groups. Belonged to it is ticked,
 * found by the search of each row's own record; Made on the survivor after the
 * merge and Can't tell are unticked, each with its reason. The press moves the
 * ticked rows back; a refusal stands inside the window (decision 3060).
 */
export function UndoMergeWindow({ mergeId, apiBaseUrl = '', onCancel, onUndone }: {
  mergeId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onUndone: (answer: UndoMergeAnswer) => void;
}) {
  const navigate = useNavigate();
  /** 🔒 The Zoho references the person chose for the returned record; null keeps the search's own choice (decision 7500). */
  const [zoho, setZoho] = useState<readonly string[] | null>(null);
  const preview = useApi<UndoMergePreview>(undoMergePreviewPath(mergeId, zoho), apiBaseUrl);
  const ready = preview.state.kind === 'ready' ? preview.state.data : null;
  /** The press's answer where a Zoho reference went back, which offers Refresh from Zoho before the page opens. */
  const [done, setDone] = useState<UndoMergeAnswer | null>(null);
  /** The rows whose tick the person changed, against the tick the window opened with. */
  const [changed, setChanged] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const tickedOf = (row: UndoMergeRow): boolean => (changed.has(row.key) ? !row.ticked : row.ticked);
  const toggle = (key: string) => setChanged((now) => {
    const next = new Set(now);
    if (!next.delete(key)) next.add(key);
    return next;
  });
  const rows = ready?.rows ?? [];
  const goBack = rows.filter(tickedOf).length;
  const press = async () => {
    setBusy(true);
    setRefused(null);
    const result = await post<UndoMergeAnswer>(undoMergePath(mergeId), { rows: rows.filter(tickedOf).map((row) => row.key), ...(ready?.zoho ? { zohoRefs: ready.zoho.chosen } : {}) }, apiBaseUrl);
    setBusy(false);
    if (!result.ok) {
      setRefused(result.message);
      return;
    }
    if (result.data.refresh.length > 0) setDone(result.data);
    else onUndone(result.data);
  };
  /** 🔒 Refresh from Zoho (the owner's case of 2026-10-24): one read of each contact given back, through the import's own route, then the import page follows it. */
  const refresh = async (answer: UndoMergeAnswer) => {
    setBusy(true);
    setRefused(null);
    // 🔒 Decision 7550: a Zoho item an import merge gave back is read through the items block.
    const item = answer.refreshKind === 'Item';
    // One read at a time, stopping at the first refusal, which the refusal says with how many were read.
    for (const [read, contactId] of answer.refresh.entries()) {
      const started = await post<ImportRunView>(ZOHO_BOOKS_RUNS_PATH, { block: item ? 'Items' : 'Contacts', scopeKind: item ? 'Item' : 'Contact', scopeSourceId: contactId, overwriteLocalChanges: true }, apiBaseUrl);
      if (!started.ok) {
        setBusy(false);
        setRefused(`${fill(t('undoMerge.refreshStopped'), { done: String(read), total: String(answer.refresh.length) })} ${started.message}`);
        return;
      }
    }
    setBusy(false);
    navigate(IMPORT_SCREEN_ROUTE);
  };
  const pickZoho = (id: string, on: boolean) => {
    const now = ready?.zoho?.chosen ?? [];
    setZoho(on ? [...now.filter((one) => one !== id), id] : now.filter((one) => one !== id));
  };
  if (done) {
    return (
      <FormFrame host="window" open title={t('undoMerge.title')} onCancel={() => onUndone(done)} dataForm="undo-merge-done" act={{ label: t('undoMerge.refresh'), onAct: () => void refresh(done), disabled: busy, disabledReason: t('undoMerge.loading') }} cancelLabel={t('undoMerge.openRecord')} refusal={refused} onDismissRefusal={() => setRefused(null)} typed={false}>
        <div className={styles.body} data-undo-merge-done={done.recordId}>
          <p>{line(t('undoMerge.done.lead'), done.movedBack, t('undoMerge.done.rows'))}</p>
          <p className={styles.hint}>{t('undoMerge.done.refreshHint')}</p>
        </div>
      </FormFrame>
    );
  }
  const groupTitle = (group: UndoMergeGroup): string => {
    if (!ready) return '';
    if (group === 'belonged') return `${t('undoMerge.group.belonged')} ${ready.mergedName}`;
    if (group === 'madeAfter') return `${t('undoMerge.group.madeOn')} ${ready.intoName} ${t('undoMerge.group.afterMerge')}`;
    return t('undoMerge.group.cantTell');
  };
  const groupHint = (group: UndoMergeGroup): string | null => (group === 'belonged' ? t('undoMerge.group.belongedHint') : group === 'cantTell' ? t('undoMerge.group.cantTellHint') : null);
  return (
    <FormFrame
      host="window"
      open
      title={t('undoMerge.title')}
      onCancel={onCancel}
      width="wide"
      dataForm="undo-merge"
      act={{
        label: t('undoMerge.press'),
        onAct: () => void press(),
        disabled: busy || ready === null || ready.refusal !== null,
        disabledReason: ready?.refusal ?? t('undoMerge.loading'),
      }}
      refusal={refused}
      onDismissRefusal={() => setRefused(null)}
      typed={changed.size > 0}
    >
      <div className={styles.body} data-undo-merge={mergeId} data-undo-merge-state={preview.state.kind}>
        {preview.state.kind === 'failed' ? <AlertBanner tone="error" message={preview.state.error} action={{ label: t('async.retry'), onAction: preview.reload }} /> : null}
        {ready === null ? (preview.state.kind === 'failed' ? null : <p className={styles.hint}>{t('undoMerge.loading')}</p>) : (
          <>
            {ready.refusal ? <AlertBanner tone="info" message={ready.refusal} /> : null}
            {ready.importMerge ? (
              // 🔒 An import merge (decision 7550): the contact comes back from Zoho Books, and the fields the Merge wrote stay on the survivor.
              <>
                <p data-undo-merge-recorded="import">{ready.importMerge.kind === 'Item' ? `${ready.mergedName} ${t('undoMerge.leadImportItem')} ${ready.intoName}. ${t('undoMerge.leadImportItemTail')}` : `${ready.mergedName} ${t('undoMerge.leadImport')} ${ready.intoName}. ${t('undoMerge.leadImportTail')}`}</p>
                <p className={styles.hint} data-undo-import-wrote={ready.importMerge.wrote === null ? 'unknown' : ready.importMerge.wrote.length}>{wroteWords(ready.importMerge.wrote, ready.intoName)}</p>
                {ready.importMerge.kind === 'Contact' && ready.importMerge.wrote?.some((one) => one.field === 'displayName') ? <p className={styles.hint} data-undo-import-same-name="true">{t('undoMerge.importWroteName')}</p> : null}
                {ready.importMerge.renameTo ? <p className={styles.hint} data-undo-import-renamed="true">{[ready.intoName, t('undoMerge.importRenamedIs'), ready.importMerge.renameTo, t('undoMerge.importRenamedAgain')].join(' ')}</p> : null}
                {ready.importMerge.nameUnknown ? <p className={styles.hint} data-undo-import-name-unknown="true">{t('undoMerge.importNameUnknown')}</p> : null}
              </>
            ) : (
              <p data-undo-merge-recorded={ready.recorded ? 'true' : 'false'}>{ready.recorded ? `${ready.mergedName} ${t('undoMerge.lead')}` : ready.zoho && ready.zoho.chosen.length > 0 ? `${ready.mergedName} ${t('undoMerge.leadZoho')}` : t('undoMerge.leadEmpty')}</p>
            )}
            {ready.zoho ? (
              // 🔒 The survivor's Zoho references (decision 7500): the one the search told apart is ticked; otherwise the person picks which were the merged company's.
              <section className={styles.group} data-undo-zoho={ready.zoho.toldApart ? 'toldApart' : 'pick'}>
                <h3 className={styles.groupTitle}>{t('undoMerge.zoho.title')}</h3>
                <p className={styles.hint}>{ready.zoho.toldApart ? t('undoMerge.zoho.toldApart') : t('undoMerge.zoho.pick')}</p>
                <p className={styles.hint} data-undo-zoho-refresh-needs-import="true">{t('undoMerge.zoho.refreshNeedsImport')}</p>
                <ul className={styles.rows}>
                  {ready.zoho.choices.map((choice) => (
                    <li key={choice.id} className={styles.row} data-undo-zoho-choice={choice.sourceId} data-undo-zoho-chosen={ready.zoho!.chosen.includes(choice.id) ? 'true' : 'false'}>
                      <Checkbox label={choice.name} checked={ready.zoho!.chosen.includes(choice.id)} onChange={(on) => pickZoho(choice.id, on)} disabled={busy} size={Size.Small} />
                      <span className={styles.word}>{choice.sourceId}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {UNDO_MERGE_GROUPS.map((group) => {
              const listed = rows.filter((row) => row.group === group);
              return (
                <section key={group} className={styles.group} data-undo-group={group} data-undo-group-count={listed.length}>
                  <h3 className={styles.groupTitle}>{line(groupTitle(group), '·', listed.length)}</h3>
                  {groupHint(group) && listed.length > 0 ? <p className={styles.hint}>{groupHint(group)}</p> : null}
                  {listed.length === 0 ? <p className={styles.hint}>{t('undoMerge.group.none')}</p> : (
                    <ul className={styles.rows}>
                      {listed.map((row) => (
                        <li key={row.key} className={styles.row} data-undo-row={row.key} data-undo-ticked={tickedOf(row) ? 'true' : 'false'}>
                          <div>
                            <Checkbox label={row.label} checked={tickedOf(row)} onChange={() => toggle(row.key)} disabled={busy || row.fixed !== null} size={Size.Small} />
                            <div className={styles.detail}>{line(kindWord(row), '·', row.reason)}</div>
                          </div>
                          <span className={styles.word}>{row.fixed ? t('undoMerge.word.stays') : tickedOf(row) ? t('undoMerge.word.goesBack') : GROUP_WORD[row.group]}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
            {ready.silent > 0 ? <p className={styles.hint} data-undo-silent={ready.silent}>{line(ready.silent, t('undoMerge.silent'))}</p> : null}
            {ready.joined > 0 ? <p className={styles.hint} data-undo-joined={ready.joined}>{line(ready.joined, t('undoMerge.joined'))}</p> : null}
            {ready.gone > 0 ? <p className={styles.hint} data-undo-gone={ready.gone}>{line(ready.gone, t('undoMerge.gone'))}</p> : null}
            <p className={styles.summary} data-undo-summary={goBack}>{line(goBack, t('undoMerge.summary.goBack'), '·', rows.length - goBack, t('undoMerge.summary.stay'))}</p>
          </>
        )}
      </div>
    </FormFrame>
  );
}
